/**
 * Cookie-consent primitives.
 *
 * The choice lives in a first-party cookie rather than localStorage so it is
 * readable anywhere on the client without a server round-trip (a server read in
 * the root layout would opt every route out of static rendering), and it is
 * versioned so a future change to what we ask for can re-prompt everyone.
 *
 * Today the site sets strictly-necessary cookies only; the banner exists so
 * that optional analytics is opt-in and never loads before an explicit "accept
 * all" — see the Cookie Policy.
 */
export const CONSENT_COOKIE = "_consent"

/** Window event fired after a decision, so live components can react without a reload. */
export const CONSENT_EVENT = "consent-changed"

export const CONSENT_VERSION = 1

/** 6 months — the usual maximum retention regulators expect for a consent proof. */
export const CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180

export type ConsentChoice = "all" | "essential"

const PREFIX = `v${CONSENT_VERSION}:`

export const serializeConsent = (choice: ConsentChoice): string =>
  `${PREFIX}${choice}`

/**
 * Reads a stored value. Returns null for anything we did not write — including
 * values from an older consent version, which must be asked again.
 */
export const parseConsent = (raw?: string | null): ConsentChoice | null => {
  if (!raw || !raw.startsWith(PREFIX)) {
    return null
  }

  const choice = raw.slice(PREFIX.length)

  return choice === "all" || choice === "essential" ? choice : null
}

/** Parses a `document.cookie` string. Null means "not decided" (or stale). */
export const readConsentCookie = (
  cookieString: string | null | undefined
): ConsentChoice | null => {
  if (!cookieString) {
    return null
  }

  const match = new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`).exec(
    cookieString
  )

  if (!match) {
    return null
  }

  try {
    return parseConsent(decodeURIComponent(match[1]))
  } catch {
    return null
  }
}

export const buildConsentCookie = (
  choice: ConsentChoice,
  options?: { secure?: boolean }
): string =>
  [
    `${CONSENT_COOKIE}=${encodeURIComponent(serializeConsent(choice))}`,
    "path=/",
    `max-age=${CONSENT_MAX_AGE_SECONDS}`,
    "samesite=lax",
    ...(options?.secure ? ["secure"] : []),
  ].join("; ")

/** Expires the stored choice, sending the visitor back to "undecided". */
export const clearConsentCookie = (options?: { secure?: boolean }): string =>
  [
    `${CONSENT_COOKIE}=`,
    "path=/",
    "max-age=0",
    "samesite=lax",
    ...(options?.secure ? ["secure"] : []),
  ].join("; ")
